import { IntegrationEvent } from "./IntegrationEvent";
import { IntegrationRequest } from "./IntegrationRequest";

/** Event handler type. */
type IntegrationEventHandler<T extends IntegrationEvent<any>> = (payload: T["payload"]) => Promise<void>;

/** Request handler type. */
type IntegrationRequestHandler<T extends IntegrationRequest<any, any>, R = T["responseType"]> = (payload: T["payload"]) => Promise<R>;

/** Integration event constructor type. */
type IntegrationEventConstructor<T extends IntegrationEvent<any>> = (new (payload: T["payload"]) => T) & { readonly eventKey: string };

/** Integration request constructor type. */
type IntegrationRequestConstructor<T extends IntegrationRequest<any, any>> = (new (payload: T["payload"]) => T) & { readonly requestKey: string };

/** Singleton IntegrationBus for managing events and requests. */
class IntegrationBus {
    /** Map of event/request keys to their handlers. */
    private readonly listeners = new Map<string, Array<IntegrationEventHandler<any> | IntegrationRequestHandler<any, any>>>();

    /**
     * Register an event handler.
     * @param event The event constructor.
     * @param handler The event handler.
     */
    public on<T extends IntegrationEvent<any>>(event: IntegrationEventConstructor<T>, handler: IntegrationEventHandler<T>): void;

    /**
     * Register a request handler.
     * @param request The request constructor.
     * @param handler The request handler.
     */
    public on<T extends IntegrationRequest<any, any>>(request: IntegrationRequestConstructor<T>, handler: IntegrationRequestHandler<T>): void;

    /**
     * Register an event or request handler.
     * @param eventOrRequest The event or request constructor.
     * @param handler The event or request handler.
     */
    public on<T extends IntegrationEvent<any>, R extends IntegrationRequest<any, any>>(
        eventOrRequest: IntegrationEventConstructor<T> | IntegrationRequestConstructor<R>,
        handler: IntegrationEventHandler<T> | IntegrationRequestHandler<R>
    ): void {
        const key = this.validateKey(eventOrRequest);
        const handlers = this.listeners.get(key) || [];
        handlers.push(handler);
        this.listeners.set(key, handlers);
    }

    /**
     * Unregister an event handler.
     * @param event The event constructor.
     * @param handler The event handler to remove.
     */
    public off<T extends IntegrationEvent<any>>(event: IntegrationEventConstructor<T>, handler: IntegrationEventHandler<T>): void;

    /**
     * Unregister a request handler.
     * @param request The request constructor.
     * @param handler The request handler to remove.
     */
    public off<T extends IntegrationRequest<any, any>>(request: IntegrationRequestConstructor<T>, handler: IntegrationRequestHandler<T>): void;

    /**
     * Unregister an event or request handler.
     * @param eventOrRequest The event or request constructor.
     * @param handler The event or request handler to remove.
     */
    public off<T extends IntegrationEvent<any>, R extends IntegrationRequest<any, any>>(
        eventOrRequest: IntegrationEventConstructor<T> | IntegrationRequestConstructor<R>,
        handler: IntegrationEventHandler<T> | IntegrationRequestHandler<R>
    ): void {
        const key = this.validateKey(eventOrRequest);
        const handlers = this.listeners.get(key) || [];
        this.listeners.set(key, handlers.filter(p => p !== handler));
    }

    /**
     * Emit an event.
     * @param event The event instance to emit.
     */
    public emit<T extends IntegrationEvent<any>>(event: T): void {
        const eventKey = this.validateEventKey(event);
        const handlers = this.listeners.get(eventKey) || [];
        handlers.forEach(handler => {
            handler(event.payload).catch(() => {
                // silently ignore errors in fire-and-forget emit
                console.error(`Error in event handler for ${eventKey}`);
            });
        });
    }

    /**
     * Wait for all handlers of an event to complete.
     * @param event The event instance to wait for.
     * @return A promise that resolves when all handlers have completed.
     */
    public async waitAsync<T extends IntegrationEvent<any>>(event: T): Promise<void> {
        const eventKey = this.validateEventKey(event);
        const handlers = this.listeners.get(eventKey) || [];
        await Promise.all(handlers.map(handler => handler(event.payload)));
    }

    /**
     * Send a request and gather responses from all listeners.
     * @param event The request instance to send.
     * @return An array of responses from all listeners.
     */
    public async fanoutAsync<T extends IntegrationRequest<any, any>>(event: T): Promise<Awaited<T["responseType"]>[]> {
        const requestKey = this.validateRequestKey(event);
        const handlers = this.listeners.get(requestKey) || [];
        return Promise.all(handlers.map(handler => (handler as IntegrationRequestHandler<T, T["responseType"]>)(event.payload)));
    }

    /**
     * Validate the static key of an event or request constructor.
     * @param eventOrRequest The event or request constructor.
     * @returns The key if valid.
     */
    private validateKey<T extends IntegrationEvent<any>, R extends IntegrationRequest<any, any>>(eventOrRequest: IntegrationEventConstructor<T> | IntegrationRequestConstructor<R>): string {
        if ("eventKey" in eventOrRequest)
            return eventOrRequest.eventKey;
        else if ("requestKey" in eventOrRequest)
            return eventOrRequest.requestKey;

        throw new Error("Events and requests must be created via IntegrationBusFactory.createEvent or IntegrationBusFactory.createRequest to have a valid key.");
    }

    /**
     * Validate the static eventKey of an event constructor.
     * @param event The event constructor.
     * @returns The eventKey if valid.
     */
    private validateEventKey<T extends IntegrationEvent<any>>(event: T): string {
        const eventKey = (event.constructor as IntegrationEventConstructor<T>).eventKey;
        if (!eventKey)
            throw new Error("Events must be created via IntegrationBusFactory.createEvent to have a valid eventKey.");

        return eventKey;
    }

    /**
     * Validate the static requestKey of a request constructor.
     * @param request The request constructor.
     * @returns The requestKey if valid.
     */
    private validateRequestKey<T extends IntegrationRequest<any, any>>(request: T): string {
        const requestKey = (request.constructor as IntegrationRequestConstructor<T>).requestKey;
        if (!requestKey)
            throw new Error("Requests must be created via IntegrationBusFactory.createRequest to have a valid requestKey.");

        return requestKey;
    }
}

const integrationBus = new IntegrationBus();

export {
    integrationBus,
    IntegrationEventConstructor,
    IntegrationEventHandler,
    IntegrationRequestConstructor,
    IntegrationRequestHandler
};
